import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Gift, 
  Share2, 
  Copy, 
  CheckCircle, 
  MessageCircle,
  Zap,
  Share
} from 'lucide-react';
// import { addBonusAnalyses, getStoredSubscription } from '../utils/subscriptionUtils';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../hooks/useAuth';


interface EarnAnalysesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EarnAnalysesModal: React.FC<EarnAnalysesModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [completedActions, setCompletedActions] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation();
  const { addBonusAnalyses,subscription } = useSubscription();
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const shareUrl = 'https://www.cvmatch.io';
  const shareText = t('earn_analyses.share_text');

  const actions = [
    {
      id: 'linkedin',
      title: t('earn_analyses.actions.linkedin.title'),
      description: t('earn_analyses.actions.linkedin.description'),
      icon: <Share2 className="w-5 h-5" />,
      reward: t('earn_analyses.reward_1'),
      color: 'from-blue-500 to-blue-600',
      action: async () => {
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        window.open(linkedinUrl, '_blank');
        // markActionCompleted('linkedin');
        // Add bonus analysis immediately
        try {
          const result = await addBonusAnalyses(1, 'linkedin');
          if (result.error) {
            console.error('Error adding bonus analyses for LinkedIn:', result.error);
            setError(typeof result.error === 'string' ? result.error : 'An error occurred');
          }
        } catch (error) {
          setError('An error occurred');
        }
      }
    },
    {
      id: 'instagram',
      title: t('earn_analyses.actions.instagram.title'),
      description: t('earn_analyses.actions.instagram.description'),
      icon: <MessageCircle className="w-5 h-5" />,
      reward: t('earn_analyses.reward_1'),
      color: 'from-pink-500 to-purple-600',
      action: async () => {
        // Copy link for Instagram
        navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        // markActionCompleted('instagram');
        // Add bonus analysis immediately
        try {
          const result = await addBonusAnalyses(1, 'instagram');
          if (result.error) {
            console.error('Error adding bonus analyses for Instagram:', result.error);
            setError(typeof result.error === 'string' ? result.error : 'An error occurred');
          }
        } catch (error) {
          setError('An error occurred');
        }
      }
    },
    // {
    //   id: 'email',
    //   title: t('earn_analyses.actions.email.title'),
    //   description: t('earn_analyses.actions.email.description'),
    //   icon: <Users className="w-5 h-5" />,
    //   reward: t('earn_analyses.reward_1'),
    //   color: 'from-green-500 to-green-600',
    //   action: async () => {
    //     const emailSubject = t('earn_analyses.email.subject');
    //     const emailBody = t('earn_analyses.email.body', { shareText, shareUrl });
    //     const mailtoUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    //     // markActionCompleted('email');
    //     // Add bonus analysis immediately
    //     try {
    //       const result = await addBonusAnalyses(1, 'email');
    //       if (result.error) {
    //         console.error('Error adding bonus analyses for Email:', result.error);
    //         setError(typeof result.error === 'string' ? result.error : 'An error occurred');
    //       }
    //     } catch (error) {
    //       setError('An error occurred');
    //     } 
    //     window.open(mailtoUrl);
    //   }
    // },
    // {
    //   id: 'review',
    //   title: t('earn_analyses.actions.review.title'),
    //   description: t('earn_analyses.actions.review.description'),
    //   icon: <Star className="w-5 h-5" />,
    //   reward: t('earn_analyses.reward_2'),
    //   color: 'from-yellow-500 to-orange-500',
    //   action: async () => {
    //     // Simulate opening review page
    //     alert(t('earn_analyses.review_success'));
    //     // markActionCompleted('review');
    //     // Add bonus analysis immediately (review gives 2 analyses)
    //     try {
    //       const result = await addBonusAnalyses(2, 'review');
    //       if (result.error) {
    //         console.error('Error adding bonus analyses for Review:', result.error);
    //         setError(typeof result.error === 'string' ? result.error : 'An error occurred');
    //       }
    //     } catch (error) {
    //       setError('An error occurred');
    //     }
    //   }
    // },
    {
      id: 'facebook',
      title: t('earn_analyses.actions.facebook.title'),
      description: t('earn_analyses.actions.facebook.description'),
      icon: <Share className="w-5 h-5" />,
      reward: t('earn_analyses.reward_1'),
      color: 'from-purple-500 to-blue-600',
      action: async () => {
        navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        // markActionCompleted('instagram');
        // Add bonus analysis immediately
        try {
          const result = await addBonusAnalyses(1, 'facebook');
          if (result.error) {
            console.error('Error adding bonus analyses for Facebook:', result.error);
            setError(typeof result.error === 'string' ? result.error : 'An error occurred');
          }
        } catch (error) {
          setError('An error occurred');
        }
      }
    }
  ];

  // const markActionCompleted = (actionId: string) => {
  //   if (!completedActions.includes(actionId)) {
  //     setCompletedActions(prev => [...prev, actionId]);
  //   }
  // };



  const claimRewards = async () => {
    if (completedActions.length === 0) return;

    setIsProcessing(true);
    
    // Simuler le traitement
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Bonus analyses are already added when actions are completed
    // Just close the modal and show success
    setIsProcessing(false);
    onSuccess();
    onClose();
  };

  const totalReward = completedActions.reduce((total, actionId) => {
    const action = actions.find(a => a.id === actionId);
    return total + (action?.id === 'review' ? 2 : 1);
  }, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mr-4">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('earn_analyses.title')}</h2>
              <p className="text-gray-600 mt-1">{t('earn_analyses.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Explanation */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-200">
            <div className="flex items-start">
              <Zap className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-900 mb-1">{t('earn_analyses.how_it_works.title')}</h3>
                <p className="text-sm text-green-800">
                  {t('earn_analyses.how_it_works.description')}
                </p>
              </div>
            </div>
          </div>

          {/* Available actions */}
          <div className="space-y-4 mb-6">
            {actions.map((action) => {
              const isCompleted = subscription?.actions_completed?.includes(action.id);
              
              return (
                <div
                  key={action.id}
                  className={`relative rounded-xl border-2 p-4 transition-all ${
                    isCompleted 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r ${action.color} text-white`}>
                        {action.icon}
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">{action.title}</h4>
                        <p className="text-sm text-gray-600">{action.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="font-bold text-green-600">{action.reward}</div>
                        <div className="text-xs text-gray-500">{t('earn_analyses.bonus')}</div>
                      </div>
                      
                      {isCompleted ? (
                        <div className="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                      ) : (
                        <button
                          onClick={action.action}
                          disabled={action.id === 'instagram' || action.id === 'facebook' || action.id === 'linkedin'}
                          className={`px-4 py-2 rounded-lg font-medium text-white transition-all transform hover:scale-105 bg-gradient-to-r ${action.color} hover:shadow-lg ${
                            (action.id === 'instagram' || action.id === 'facebook' || action.id === 'linkedin')
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                        >
                          {action.id === 'instagram' || action.id === 'facebook' ? t('earn_analyses.copy') : t('earn_analyses.share')}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {isCompleted && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✅ {t('earn_analyses.completed')}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {error && (
            <div className="bg-red-50 rounded-lg p-4 mb-6 border border-red-200">
              <p className="text-red-800">{error}</p>
            </div>
          )}
          {/* Special instructions */}
          {/* <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">{t('earn_analyses.special_instructions.title')}</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <div className="flex items-start">
                <span className="font-medium mr-2">Instagram:</span>
                <span>{t('earn_analyses.special_instructions.instagram')}</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-2">Email:</span>
                <span>{t('earn_analyses.special_instructions.email')}</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-2">{t('earn_analyses.special_instructions.review_label')}:</span>
                <span>{t('earn_analyses.special_instructions.review')}</span>
              </div>
            </div>
          </div> */}

          {/* Share link */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">{t('earn_analyses.share_link.title')}</h4>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {t('earn_analyses.copied')} !
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    {t('earn_analyses.copy')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Summary and validation */}
          {completedActions.length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-green-900">{t('earn_analyses.completed_actions.title')}</h4>
                  <p className="text-sm text-green-800">
                    {t('earn_analyses.completed_actions.description', { count: totalReward })}
                  </p>
                </div>
                
                <button
                  onClick={claimRewards}
                  disabled={isProcessing}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('earn_analyses.processing')}...
                    </>
                  ) : (
                    <>
                      <Gift className="w-5 h-5 mr-2" />
                      {t('earn_analyses.claim_bonus')}
                    </>
                  )}
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {completedActions.map(actionId => {
                  const action = actions.find(a => a.id === actionId);
                  return action ? (
                    <span key={actionId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {action.title} {action.reward}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p>💡 <strong>{t('earn_analyses.tip.title')}:</strong> {t('earn_analyses.tip.description')}</p>
            </div>
            
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              {t('earn_analyses.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarnAnalysesModal;